import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { toast } from "sonner";
import { Flag } from "lucide-react";
import { reportContent, REPORT_REASON_OPTIONS } from "../lib/moderation";

/**
 * ReportDialog — one component, used from anywhere a user needs to report
 * content: chat messages, waste posts, bin listings, or another user.
 *
 * Props:
 *   open, onOpenChange     Standard Radix dialog control.
 *   userId                 Reporter's id (current user).
 *   targetKind             'user' | 'message' | 'waste_post' | 'bin_listing'.
 *   targetId               id of the thing being reported.
 *   targetLabel            Human-readable label shown at the top so the user
 *                          is certain what they're reporting.
 *   onReported             Optional callback fired after successful submit.
 */
export default function ReportDialog({
  open,
  onOpenChange,
  userId,
  targetKind,
  targetId,
  targetLabel,
  onReported,
}) {
  const [reason, setReason] = useState("");
  const [details, setDetails] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setReason("");
    setDetails("");
    setSubmitting(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!reason) {
      toast.error("Please pick a reason");
      return;
    }
    setSubmitting(true);
    try {
      const res = await reportContent({ userId, targetKind, targetId, reason, details });
      toast.success(
        res.duplicate
          ? "You've already reported this — thanks, our team is on it."
          : "Report submitted. Our team reviews reports within 24 hours.",
      );
      reset();
      onOpenChange(false);
      if (onReported) onReported();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit report");
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) reset();
        onOpenChange(o);
      }}
    >
      <DialogContent data-testid="report-dialog" className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" style={{ fontFamily: "Outfit" }}>
            <Flag className="w-5 h-5 text-[#D67B54]" /> Report {kindLabel(targetKind)}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          {targetLabel && (
            <div className="text-xs text-[#6B6965] bg-[#F6F4F0] rounded-lg px-3 py-2">
              Reporting: <span className="text-[#1B3624] font-medium">{targetLabel}</span>
            </div>
          )}

          <div>
            <Label className="text-sm">Reason</Label>
            <div data-testid="report-reasons" className="mt-2 space-y-2">
              {REPORT_REASON_OPTIONS.map((r) => (
                <label
                  key={r.value}
                  data-testid={`report-reason-${r.value}`}
                  className={`flex items-start gap-2 border rounded-lg px-3 py-2 cursor-pointer transition ${
                    reason === r.value
                      ? "border-[#1B3624] bg-[#1B3624]/5"
                      : "border-[#E2DFD8] hover:border-[#1B3624]/50"
                  }`}
                >
                  <input
                    type="radio"
                    name="report-reason"
                    value={r.value}
                    checked={reason === r.value}
                    onChange={(e) => setReason(e.target.value)}
                    className="mt-0.5 accent-[#1B3624]"
                  />
                  <span className="text-sm">{r.label}</span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <Label htmlFor="report-details" className="text-sm">
              Additional details <span className="text-[#6B6965]">(optional, max 2000 chars)</span>
            </Label>
            <Textarea
              id="report-details"
              data-testid="report-details"
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              placeholder="Anything else our moderators should know?"
              rows={4}
              maxLength={2000}
              className="mt-1 rounded-xl"
            />
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              data-testid="report-cancel"
              onClick={() => onOpenChange(false)}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              data-testid="report-submit"
              disabled={submitting || !reason}
              className="rounded-full bg-[#1B3624] hover:bg-[#274d33] text-white"
            >
              {submitting ? "Submitting…" : "Submit report"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function kindLabel(kind) {
  switch (kind) {
    case "user":
      return "user";
    case "message":
      return "message";
    case "waste_post":
      return "waste post";
    case "bin_listing":
      return "bin listing";
    default:
      return "content";
  }
}
